import type { ComponentRef, PageRef } from '@wix/editor-platform-sdk-types'
import type { DpVariantFilter } from '@wix/wix-data-client-types'
import type {
  DynamicPageRouterData,
  EditorSDK,
  RouterConfig,
} from '@wix/wix-data-client-common-standalone'
import { ROUTER_DATASET } from '@wix/wix-data-client-types'
import { findPageRefByPageRole, resolveBasePageRole } from './router-config'
import { deserializeVariantFilter } from './deserializeVariantFilter'

export { deserializeVariantFilter } from './deserializeVariantFilter'

export const getVariantRules = ({
  pageRole,
  routerConfig,
}: {
  pageRole: string
  routerConfig: RouterConfig
}): DpVariantFilter[] => {
  const rules = routerConfig.rules
  if (!rules) {
    return []
  }
  const variantRuleId = Object.keys(rules).find(
    ruleId => rules[ruleId].pageRole === pageRole,
  )
  if (!variantRuleId) {
    return []
  }

  return deserializeVariantFilter(rules[variantRuleId].filter)
}

export const isDpVariant = ({
  pageRole,
  routerConfig,
}: {
  pageRole: string
  routerConfig: RouterConfig
}) => {
  const baseRole = resolveBasePageRole({
    routerConfig,
    pageRole,
  })

  return baseRole !== pageRole
}

export const hasDpVariants = ({
  pageRole,
  routerConfig,
}: {
  pageRole: string
  routerConfig: RouterConfig
}) => Boolean(routerConfig.roleVariations?.[pageRole]?.length)

const getPageRole = (pageId: string, routerData: DynamicPageRouterData) =>
  routerData.pages.find(p => p.pageRef.id === pageId)?.pageRoles[0]

const getRouterDataForPage = async ({
  pageRef,
  editorSdkProxy,
}: {
  pageRef: PageRef
  editorSdkProxy: EditorSDK
}) => {
  const routerRef = await editorSdkProxy.document.routers.getByPage({
    pageRef,
  })
  if (!routerRef) {
    return null
  }
  const routerData = await editorSdkProxy.routers.get<RouterConfig>({
    routerRef,
  })
  if (!routerData) {
    return null
  }
  const pageRole = getPageRole(pageRef.id, routerData)
  if (!pageRole) {
    return null
  }

  return { routerData, pageRole }
}

export const getDpVariantsCount = ({
  pageRef,
  routerData,
}: {
  pageRef: PageRef
  routerData: DynamicPageRouterData
}): number => {
  const pageRole = getPageRole(pageRef.id, routerData)
  if (!pageRole) {
    return 0
  }
  return routerData.config.roleVariations?.[pageRole]?.length ?? 0
}

export const resolveIsDpVariant = async ({
  pageRef,
  editorSdkProxy,
}: {
  pageRef: PageRef
  editorSdkProxy: EditorSDK
}): Promise<boolean> => {
  const result = await getRouterDataForPage({ pageRef, editorSdkProxy })
  if (!result) {
    return false
  }

  return isDpVariant({
    pageRole: result.pageRole,
    routerConfig: result.routerData.config,
  })
}

export const resolveHasDpVariants = async ({
  pageRef,
  editorSdkProxy,
}: {
  pageRef: PageRef
  editorSdkProxy: EditorSDK
}): Promise<boolean> => {
  const result = await getRouterDataForPage({ pageRef, editorSdkProxy })
  if (!result) {
    return false
  }

  return hasDpVariants({
    pageRole: result.pageRole,
    routerConfig: result.routerData.config,
  })
}

export const getOriginalPageRefForVariant = async ({
  variantPageRef,
  editorSdkProxy,
}: {
  variantPageRef: PageRef
  editorSdkProxy: EditorSDK
}): Promise<PageRef> => {
  const result = await getRouterDataForPage({
    pageRef: variantPageRef,
    editorSdkProxy,
  })

  if (!result) {
    throw new Error('Failed to resolve original page ref from variant page ref')
  }

  const basePageRole = resolveBasePageRole({
    routerConfig: result.routerData.config,
    pageRole: result.pageRole,
  })

  const originalPageRef = findPageRefByPageRole(result.routerData, basePageRole)

  if (!originalPageRef) {
    throw new Error('Failed to resolve original page ref from variant page ref')
  }

  return originalPageRef
}

export const findVariantSiblingRouterDatasetRefs = async ({
  editorSdkProxy,
  controllerRef,
}: {
  editorSdkProxy: EditorSDK
  controllerRef: ComponentRef
}): Promise<ComponentRef[]> => {
  const pageRef = await editorSdkProxy.components.getPage({
    componentRef: controllerRef,
  })
  const result = await getRouterDataForPage({ pageRef, editorSdkProxy })
  if (!result) {
    return []
  }

  const { routerData, pageRole: currentPageRole } = result
  const routerConfig = routerData.config

  const basePageRole = resolveBasePageRole({
    routerConfig,
    pageRole: currentPageRole,
  })

  const allRelatedRoles = [basePageRole]
  const variantRuleIds = routerConfig.roleVariations?.[basePageRole] ?? []
  for (const ruleId of variantRuleIds) {
    const rule = routerConfig.rules?.[ruleId]
    if (rule?.pageRole) {
      allRelatedRoles.push(rule.pageRole)
    }
  }

  const siblingRoles = allRelatedRoles.filter(role => role !== currentPageRole)

  const refs = await Promise.all(
    siblingRoles.map(async role => {
      const siblingPageRef = findPageRefByPageRole(routerData, role)
      if (!siblingPageRef) {
        return null
      }

      const controllers = await editorSdkProxy.controllers.listControllers({
        pageRef: siblingPageRef,
      })
      for (const { controllerRef: ref } of controllers) {
        const data = await editorSdkProxy.controllers.getData({
          controllerRef: ref,
        })
        if (data.type === ROUTER_DATASET) {
          return ref
        }
      }
      return null
    }),
  )
  return refs.filter((ref): ref is ComponentRef => ref !== null)
}
